import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isNativePlatform } from '@/services/platformService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Crown, Copy, Check, Users, DollarSign, TrendingUp, LogOut, Sparkles, Calculator, History, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { Database } from '@/integrations/supabase/types';

type AmbassadorReferral = Database['public']['Tables']['ambassador_referrals']['Row'];
type AmbassadorPayout = Database['public']['Tables']['ambassador_payouts']['Row'];
type AmbassadorPlatform = Database['public']['Tables']['ambassador_platforms']['Row'];

interface AmbassadorData {
  id: string;
  full_name: string;
  tier: string;
  referral_code: string;
  status: string;
  total_followers: number;
  paypal_email: string;
  created_at: string;
}

const COMMISSION_BY_TIER: Record<string, { plan: string; price: number; cpa: number; monthly: number }[]> = {
  mega: [
    { plan: 'Standard', price: 76.99, cpa: 9.24, monthly: 0.77 },
    { plan: 'Premium', price: 109.89, cpa: 13.19, monthly: 1.10 },
    { plan: 'Ultimate', price: 219.89, cpa: 26.39, monthly: 2.20 },
  ],
  macro: [
    { plan: 'Standard', price: 76.99, cpa: 7.70, monthly: 0.64 },
    { plan: 'Premium', price: 109.89, cpa: 10.99, monthly: 0.92 },
    { plan: 'Ultimate', price: 219.89, cpa: 21.99, monthly: 1.83 },
  ],
  micro: [
    { plan: 'Standard', price: 76.99, cpa: 6.16, monthly: 0.51 },
    { plan: 'Premium', price: 109.89, cpa: 8.79, monthly: 0.73 },
    { plan: 'Ultimate', price: 219.89, cpa: 17.59, monthly: 1.47 },
  ],
  nano: [
    { plan: 'Standard', price: 76.99, cpa: 4.62, monthly: 0.38 },
    { plan: 'Premium', price: 109.89, cpa: 6.59, monthly: 0.55 },
    { plan: 'Ultimate', price: 219.89, cpa: 13.19, monthly: 1.10 },
  ],
};

const TIER_CONFIG: Record<string, { label: string; gradient: string; badge: string; glow: string; next?: string; nextReq?: string }> = {
  nano: { label: 'Nano', gradient: 'from-emerald-400 to-green-500', badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', glow: 'shadow-[0_0_20px_rgba(16,185,129,0.15)]', next: 'Micro', nextReq: '10,000 followers' },
  micro: { label: 'Micro', gradient: 'from-blue-400 to-indigo-500', badge: 'bg-blue-500/15 text-blue-400 border-blue-500/30', glow: 'shadow-[0_0_20px_rgba(59,130,246,0.15)]', next: 'Macro', nextReq: '50,000 followers' },
  macro: { label: 'Macro', gradient: 'from-amber-400 to-orange-500', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30', glow: 'shadow-[0_0_20px_rgba(245,158,11,0.15)]', next: 'Mega', nextReq: '500,000 followers' },
  mega: { label: 'Mega', gradient: 'from-purple-400 to-pink-500', badge: 'bg-purple-500/15 text-purple-400 border-purple-500/30', glow: 'shadow-[0_0_20px_rgba(168,85,247,0.15)]' },
};

const AmbassadorDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [ambassador, setAmbassador] = useState<AmbassadorData | null>(null);
  const [referrals, setReferrals] = useState<AmbassadorReferral[]>([]);
  const [payouts, setPayouts] = useState<AmbassadorPayout[]>([]);
  const [platforms, setPlatforms] = useState<AmbassadorPlatform[]>([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chartMode, setChartMode] = useState<'referrals' | 'earnings'>('referrals');
  const [referralPage, setReferralPage] = useState(0);
  const REFERRALS_PER_PAGE = 10;

  // Calculator state — separate monthly vs annual subscribers per plan
  const [calcStdMonthly, setCalcStdMonthly] = useState('');
  const [calcStdAnnual, setCalcStdAnnual] = useState('');
  const [calcPremMonthly, setCalcPremMonthly] = useState('');
  const [calcPremAnnual, setCalcPremAnnual] = useState('');
  const [calcUltMonthly, setCalcUltMonthly] = useState('');
  const [calcUltAnnual, setCalcUltAnnual] = useState('');

  useEffect(() => {
    if (isNativePlatform()) { navigate('/', { replace: true }); return; }
    if (!user) { navigate('/ambassador-login', { replace: true }); return; }
    const fetchAll = async () => {
      const { data: appData } = await supabase
        .from('ambassador_applications')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!appData || appData.status !== 'approved') {
        navigate('/ambassador-login', { replace: true });
        return;
      }
      setAmbassador(appData as unknown as AmbassadorData);

      // Fetch referrals, payouts, platforms in parallel
      const [refRes, payRes, platRes] = await Promise.all([
        supabase.from('ambassador_referrals').select('*').eq('ambassador_id', appData.id).order('created_at', { ascending: false }),
        supabase.from('ambassador_payouts').select('*').eq('ambassador_id', appData.id).order('created_at', { ascending: false }),
        supabase.from('ambassador_platforms').select('*').eq('ambassador_id', appData.id),
      ]);
      setReferrals(refRes.data || []);
      setPayouts(payRes.data || []);
      setPlatforms(platRes.data || []);
      setLoading(false);
    };
    fetchAll();
  }, [user, navigate]);

  // KPI calculations
  const kpis = useMemo(() => {
    const now = new Date();
    const thisMonth = referrals.filter(r => {
      const d = new Date(r.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const approvedCommissions = referrals.filter(r => r.status === 'approved').reduce((s, r) => s + Number(r.commission_amount), 0);
    const pendingBalance = referrals.filter(r => r.status === 'approved' || r.status === 'hold').reduce((s, r) => s + Number(r.commission_amount), 0);
    const totalPaid = payouts.filter(p => p.status === 'processed').reduce((s, p) => s + Number(p.amount), 0);
    const unpaidBalance = approvedCommissions - totalPaid;
    const payoutProgress = Math.min((unpaidBalance / 50) * 100, 100);
    return { thisMonth: thisMonth.length, approvedCommissions, pendingBalance: unpaidBalance, totalPaid, payoutProgress };
  }, [referrals, payouts]);

  // Chart data - last 12 months
  const chartData = useMemo(() => {
    const months: { month: string; referrals: number; earnings: number }[] = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleDateString('en', { month: 'short' });
      const monthRefs = referrals.filter(r => {
        const rd = new Date(r.created_at);
        return rd.getMonth() === d.getMonth() && rd.getFullYear() === d.getFullYear();
      });
      months.push({
        month: label,
        referrals: monthRefs.length,
        earnings: monthRefs.reduce((s, r) => s + Number(r.commission_amount), 0),
      });
    }
    return months;
  }, [referrals]);

  // Calculator
  // Logic:
  //  - Monthly subscriber → pays `monthly` USD/month recurring (while subscribed).
  //  - Annual subscriber → pays `cpa` USD one-time per year (lump sum on each yearly renewal).
  // Monthly projection = sum of monthly recurring commissions + (annual commissions / 12 averaged).
  // Annual projection  = (monthly commissions × 12) + (annual one-time commissions × 1).
  const calcResult = useMemo(() => {
    if (!ambassador) return { monthly: 0, annual: 0, monthlyRecurring: 0, annualOneTime: 0 };
    const c = COMMISSION_BY_TIER[ambassador.tier] || COMMISSION_BY_TIER.nano;
    const stdM = parseInt(calcStdMonthly) || 0;
    const stdA = parseInt(calcStdAnnual) || 0;
    const premM = parseInt(calcPremMonthly) || 0;
    const premA = parseInt(calcPremAnnual) || 0;
    const ultM = parseInt(calcUltMonthly) || 0;
    const ultA = parseInt(calcUltAnnual) || 0;

    // Recurring monthly income from monthly subscribers
    const monthlyRecurring =
      stdM * c[0].monthly + premM * c[1].monthly + ultM * c[2].monthly;

    // One-time annual commission from annual subscribers (paid once per year)
    const annualOneTime =
      stdA * c[0].cpa + premA * c[1].cpa + ultA * c[2].cpa;

    // Monthly projection = recurring + averaged annual (annualOneTime / 12)
    const monthly = monthlyRecurring + annualOneTime / 12;
    // Annual projection = recurring × 12 + annual one-time
    const annual = monthlyRecurring * 12 + annualOneTime;

    return { monthly, annual, monthlyRecurring, annualOneTime };
  }, [calcStdMonthly, calcStdAnnual, calcPremMonthly, calcPremAnnual, calcUltMonthly, calcUltAnnual, ambassador]);

  if (isNativePlatform() || loading || !ambassador) {
    return (
      <div className="min-h-screen bg-[#07070d] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-amber-500/40 border-t-amber-400 rounded-full animate-spin" />
      </div>
    );
  }

  const copyCode = () => {
    if (!ambassador.referral_code) return;
    navigator.clipboard.writeText(ambassador.referral_code);
    setCopied(true);
    toast({ title: t('ambassador.dashboard.codeCopied') });
    setTimeout(() => setCopied(false), 2000);
  };

  const tierCfg = TIER_CONFIG[ambassador.tier] || TIER_CONFIG.nano;
  const commissions = COMMISSION_BY_TIER[ambassador.tier] || COMMISSION_BY_TIER.nano;

  const handleSignOut = async () => {
    await signOut();
    navigate('/ambassador-login', { replace: true });
  };

  const paginatedReferrals = referrals.slice(referralPage * REFERRALS_PER_PAGE, (referralPage + 1) * REFERRALS_PER_PAGE);
  const totalRefPages = Math.ceil(referrals.length / REFERRALS_PER_PAGE);

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      hold: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
      approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
      paid: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
      cancelled: 'bg-red-500/15 text-red-400 border-red-500/30',
      pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
      processed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    };
    return map[status] || map.hold;
  };

  return (
    <div className="min-h-screen bg-[#07070d] text-white relative">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-gradient-to-b from-amber-900/8 to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: 'linear-gradient(rgba(255,215,0,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${tierCfg.gradient} ${tierCfg.glow}`}>
              <Crown className="h-7 w-7 text-white drop-shadow-lg" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t('ambassador.dashboard.welcome', { name: ambassador.full_name.split(' ')[0] })}</h1>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border mt-1 ${tierCfg.badge}`}>
                <Sparkles className="h-3 w-3" />
                {tierCfg.label} {t('ambassador.dashboard.ambassadorLabel', 'Ambassador')}
              </div>
            </div>
          </div>
          <Button variant="ghost" onClick={handleSignOut} className="text-gray-500 hover:text-white hover:bg-white/5">
            <LogOut className="h-4 w-4 mr-2" />
            {t('common.signOut')}
          </Button>
        </div>

        {/* Referral Code */}
        {ambassador.referral_code && (
          <div className="relative overflow-hidden bg-gradient-to-r from-amber-950/40 via-[#12121a] to-amber-950/40 border border-amber-500/15 rounded-2xl p-7 mb-8 shadow-[0_4px_40px_rgba(0,0,0,0.3)]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-amber-500/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
            <p className="text-amber-400/70 text-xs font-semibold tracking-[0.2em] uppercase mb-3">{t('ambassador.dashboard.yourCode')}</p>
            <div className="flex items-center gap-4">
              <code className="text-3xl md:text-4xl font-bold tracking-[0.15em] text-white">{ambassador.referral_code}</code>
              <Button onClick={copyCode} variant="ghost" className="text-amber-400/60 hover:text-amber-300 hover:bg-amber-500/10 rounded-xl h-10 w-10 p-0">
                {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </Button>
            </div>
            <p className="text-gray-600 text-xs mt-3">{t('ambassador.dashboard.shareCode')}</p>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <Users className="h-5 w-5 text-amber-400" />
              <span className="text-gray-500 text-sm">{t('ambassador.dashboard.kpi.referrals')}</span>
            </div>
            <p className="text-3xl font-bold">{kpis.thisMonth}</p>
            <p className="text-gray-700 text-xs mt-1">{t('ambassador.dashboard.kpi.thisMonth')}</p>
          </div>
          <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              <span className="text-gray-500 text-sm">{t('ambassador.dashboard.kpi.earnings')}</span>
            </div>
            <p className="text-3xl font-bold">${kpis.approvedCommissions.toFixed(2)}</p>
            <p className="text-gray-700 text-xs mt-1">{t('ambassador.dashboard.kpi.accumulated')}</p>
          </div>
          <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <History className="h-5 w-5 text-blue-400" />
              <span className="text-gray-500 text-sm">{t('ambassador.dashboard.kpi.totalPaid', 'Total Paid')}</span>
            </div>
            <p className="text-3xl font-bold">${kpis.totalPaid.toFixed(2)}</p>
            <p className="text-gray-700 text-xs mt-1">{t('ambassador.dashboard.kpi.historical', 'Historical')}</p>
          </div>
          <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="h-5 w-5 text-purple-400" />
              <span className="text-gray-500 text-sm">{t('ambassador.dashboard.kpi.payoutStatus')}</span>
            </div>
            <p className="text-xl font-bold">${kpis.pendingBalance.toFixed(2)} / $50.00</p>
            <div className="w-full bg-gray-800 rounded-full h-2 mt-3">
              <div className="bg-gradient-to-r from-amber-500 to-amber-400 h-2 rounded-full transition-all" style={{ width: `${kpis.payoutProgress}%` }} />
            </div>
            <p className="text-gray-700 text-xs mt-1">{t('ambassador.dashboard.kpi.minThreshold')}</p>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white text-lg font-semibold">{t('ambassador.dashboard.chart.title')}</h3>
            <div className="flex gap-1 bg-black/30 rounded-lg p-1">
              <button onClick={() => setChartMode('referrals')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${chartMode === 'referrals' ? 'bg-amber-500/20 text-amber-300' : 'text-gray-500 hover:text-gray-300'}`}>
                {t('ambassador.dashboard.chart.referrals')}
              </button>
              <button onClick={() => setChartMode('earnings')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${chartMode === 'earnings' ? 'bg-amber-500/20 text-amber-300' : 'text-gray-500 hover:text-gray-300'}`}>
                USD
              </button>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2a" />
                <XAxis dataKey="month" stroke="#444" />
                <YAxis stroke="#444" />
                <Tooltip contentStyle={{ backgroundColor: '#12121a', border: '1px solid rgba(245,158,11,0.15)', color: '#fff', borderRadius: '12px' }} />
                <Bar dataKey={chartMode} fill="url(#barGradient)" radius={[6, 6, 0, 0]} name={chartMode === 'referrals' ? t('ambassador.dashboard.chart.referrals') : 'USD'} />
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#d97706" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Income Calculator */}
        <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="h-5 w-5 text-amber-400" />
            <h3 className="text-white text-lg font-semibold">{t('ambassador.dashboard.calculator.title', 'Income Calculator')}</h3>
          </div>
          <p className="text-gray-500 text-sm mb-2">{t('ambassador.dashboard.calculator.subtitle', 'Estimate your earnings based on expected subscribers.')}</p>
          <p className="text-gray-600 text-xs mb-5">
            {t('ambassador.dashboard.calculator.note', 'Monthly subscribers generate recurring monthly commissions. Annual subscribers generate a one-time CPA commission per yearly period.')}
          </p>

          {/* Headers */}
          <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr] gap-4 mb-2 px-1">
            <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider">{t('ambassador.dashboard.calculator.plan', 'Plan')}</div>
            <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider">
              {t('ambassador.dashboard.calculator.monthlySubs', 'Monthly subscribers')}
              <span className="block text-gray-600 text-[10px] normal-case font-normal mt-0.5">
                {t('ambassador.dashboard.calculator.recurringPerUser', 'Recurring USD/user/month')}
              </span>
            </div>
            <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider">
              {t('ambassador.dashboard.calculator.annualSubs', 'Annual subscribers')}
              <span className="block text-gray-600 text-[10px] normal-case font-normal mt-0.5">
                {t('ambassador.dashboard.calculator.cpaPerUser', 'One-time CPA USD/user/year')}
              </span>
            </div>
          </div>

          {/* Standard row */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-3 sm:gap-4 mb-3 sm:items-center">
            <div className="text-white text-sm font-medium">
              Standard
              <span className="block text-gray-600 text-[10px]">
                ${commissions[0].monthly.toFixed(2)}/mo · ${commissions[0].cpa.toFixed(2)} CPA
              </span>
            </div>
            <Input type="number" min="0" value={calcStdMonthly} onChange={(e) => setCalcStdMonthly(e.target.value)} className="bg-black/40 border-gray-700/50 text-white rounded-lg h-10 focus:border-amber-500/50" placeholder="0" />
            <Input type="number" min="0" value={calcStdAnnual} onChange={(e) => setCalcStdAnnual(e.target.value)} className="bg-black/40 border-gray-700/50 text-white rounded-lg h-10 focus:border-amber-500/50" placeholder="0" />
          </div>

          {/* Premium row */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-3 sm:gap-4 mb-3 sm:items-center">
            <div className="text-white text-sm font-medium">
              Premium
              <span className="block text-gray-600 text-[10px]">
                ${commissions[1].monthly.toFixed(2)}/mo · ${commissions[1].cpa.toFixed(2)} CPA
              </span>
            </div>
            <Input type="number" min="0" value={calcPremMonthly} onChange={(e) => setCalcPremMonthly(e.target.value)} className="bg-black/40 border-gray-700/50 text-white rounded-lg h-10 focus:border-amber-500/50" placeholder="0" />
            <Input type="number" min="0" value={calcPremAnnual} onChange={(e) => setCalcPremAnnual(e.target.value)} className="bg-black/40 border-gray-700/50 text-white rounded-lg h-10 focus:border-amber-500/50" placeholder="0" />
          </div>

          {/* Ultimate row */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-3 sm:gap-4 mb-6 sm:items-center">
            <div className="text-white text-sm font-medium">
              Ultimate
              <span className="block text-gray-600 text-[10px]">
                ${commissions[2].monthly.toFixed(2)}/mo · ${commissions[2].cpa.toFixed(2)} CPA
              </span>
            </div>
            <Input type="number" min="0" value={calcUltMonthly} onChange={(e) => setCalcUltMonthly(e.target.value)} className="bg-black/40 border-gray-700/50 text-white rounded-lg h-10 focus:border-amber-500/50" placeholder="0" />
            <Input type="number" min="0" value={calcUltAnnual} onChange={(e) => setCalcUltAnnual(e.target.value)} className="bg-black/40 border-gray-700/50 text-white rounded-lg h-10 focus:border-amber-500/50" placeholder="0" />
          </div>

          {/* Results */}
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 text-center">
              <p className="text-gray-500 text-xs mb-1">{t('ambassador.dashboard.calculator.monthlyProjection', 'Monthly Projection')}</p>
              <p className="text-2xl font-bold text-amber-400">${calcResult.monthly.toFixed(2)}</p>
              <p className="text-gray-600 text-[10px] mt-1">{t('ambassador.dashboard.calculator.monthlyHint', 'Recurring + (annual ÷ 12)')}</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 text-center">
              <p className="text-gray-500 text-xs mb-1">{t('ambassador.dashboard.calculator.annualProjection', 'Annual Projection')}</p>
              <p className="text-2xl font-bold text-amber-400">${calcResult.annual.toFixed(2)}</p>
              <p className="text-gray-600 text-[10px] mt-1">{t('ambassador.dashboard.calculator.annualHint', '(Recurring × 12) + Annual CPA')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/30 border border-gray-800/50 rounded-lg p-3 text-center">
              <p className="text-gray-600 text-[10px] uppercase tracking-wider">{t('ambassador.dashboard.calculator.recurringBreakdown', 'Monthly recurring')}</p>
              <p className="text-emerald-400 font-semibold text-sm">${calcResult.monthlyRecurring.toFixed(2)}/mo</p>
            </div>
            <div className="bg-black/30 border border-gray-800/50 rounded-lg p-3 text-center">
              <p className="text-gray-600 text-[10px] uppercase tracking-wider">{t('ambassador.dashboard.calculator.annualBreakdown', 'Annual one-time CPA')}</p>
              <p className="text-blue-400 font-semibold text-sm">${calcResult.annualOneTime.toFixed(2)}/yr</p>
            </div>
          </div>
        </div>

        {/* Referral Table */}
        <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6 mb-8">
          <h3 className="text-white text-lg font-semibold mb-4">{t('ambassador.dashboard.referrals.title', 'Referral History')}</h3>
          {referrals.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-8">{t('ambassador.dashboard.referrals.empty', 'No referrals yet. Share your code to start earning!')}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800/80">
                      <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">{t('ambassador.dashboard.referrals.date', 'Date')}</th>
                      <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">{t('ambassador.dashboard.referrals.plan', 'Plan')}</th>
                      <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">{t('ambassador.dashboard.referrals.commission', 'Commission')}</th>
                      <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">{t('ambassador.dashboard.referrals.status', 'Status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReferrals.map((ref) => {
                      const daysLeft = Math.max(0, Math.ceil((new Date(ref.maturity_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                      return (
                        <tr key={ref.id} className="border-b border-gray-800/30">
                          <td className="py-3 px-3 text-gray-300 text-sm">{new Date(ref.subscription_date).toLocaleDateString()}</td>
                          <td className="py-3 px-3 text-gray-300 text-sm capitalize">{ref.plan_type.replace('_', ' ')}</td>
                          <td className="py-3 px-3 text-amber-400 font-bold text-sm">USD {Number(ref.commission_amount).toFixed(2)}</td>
                          <td className="py-3 px-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase border ${getStatusBadge(ref.status)}`}>
                              {ref.status === 'hold' ? `Hold (${daysLeft}d)` : ref.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalRefPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="ghost" size="sm" onClick={() => setReferralPage(p => Math.max(0, p - 1))} disabled={referralPage === 0} className="text-gray-500">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-gray-500 text-xs">{referralPage + 1} / {totalRefPages}</span>
                  <Button variant="ghost" size="sm" onClick={() => setReferralPage(p => Math.min(totalRefPages - 1, p + 1))} disabled={referralPage >= totalRefPages - 1} className="text-gray-500">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Commission Table */}
        <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <h3 className="text-white text-lg font-semibold">{t('ambassador.dashboard.commissions.title')}</h3>
            <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${tierCfg.badge}`}>
              {tierCfg.label}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800/80">
                  <th className="text-left py-3 px-4 text-gray-500 text-xs font-semibold uppercase tracking-wider">{t('ambassador.dashboard.commissions.plan')}</th>
                  <th className="text-left py-3 px-4 text-gray-500 text-xs font-semibold uppercase tracking-wider">{t('ambassador.dashboard.commissions.cpa')}</th>
                  <th className="text-left py-3 px-4 text-gray-500 text-xs font-semibold uppercase tracking-wider">{t('ambassador.dashboard.commissions.monthly')}</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((row, i) => (
                  <tr key={row.plan} className={`border-b border-gray-800/30 ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
                    <td className="py-3.5 px-4 text-gray-300 font-medium">{row.plan}</td>
                    <td className="py-3.5 px-4 text-amber-400 font-bold">USD {row.cpa.toFixed(2)}</td>
                    <td className="py-3.5 px-4 text-amber-400/70">USD {row.monthly.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* My Profile & Platforms */}
        <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-5">
            <User className="h-5 w-5 text-amber-400" />
            <h3 className="text-white text-lg font-semibold">{t('ambassador.dashboard.profile.title', 'My Profile')}</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-black/20 border border-gray-800/50 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">PayPal</p>
              <p className="text-gray-300 text-sm font-medium">{ambassador.paypal_email}</p>
            </div>
            <div className="bg-black/20 border border-gray-800/50 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">{t('ambassador.dashboard.profile.level', 'Level')}</p>
              <p className="text-gray-300 text-sm font-medium">{tierCfg.label} — {ambassador.total_followers.toLocaleString()} followers</p>
              {tierCfg.next && (
                <p className="text-gray-600 text-xs mt-1">Next: {tierCfg.next} ({tierCfg.nextReq})</p>
              )}
            </div>
          </div>
          {platforms.length > 0 && (
            <div className="space-y-2">
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">{t('ambassador.dashboard.profile.platforms', 'Platforms')}</p>
              {platforms.map(p => (
                <div key={p.id} className="flex items-center justify-between bg-black/20 border border-gray-800/50 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-300">{p.platform}</span>
                    {p.is_primary && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">Primary</span>}
                    {p.verified_by_admin && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">✓ Verified</span>}
                  </div>
                  <span className="text-sm text-gray-500">{p.followers_count.toLocaleString()} followers</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payout History */}
        <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-gray-800/50 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <History className="h-5 w-5 text-amber-400" />
            <h3 className="text-white text-lg font-semibold">{t('ambassador.dashboard.payouts.title', 'Payout History')}</h3>
          </div>
          {payouts.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-8">{t('ambassador.dashboard.payouts.empty', 'No payouts yet.')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800/80">
                    <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">{t('ambassador.dashboard.payouts.date', 'Date')}</th>
                    <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">{t('ambassador.dashboard.payouts.amount', 'Amount')}</th>
                    <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">{t('ambassador.dashboard.payouts.status', 'Status')}</th>
                    <th className="text-left py-3 px-3 text-gray-500 text-xs font-semibold uppercase">PayPal</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map(p => (
                    <tr key={p.id} className="border-b border-gray-800/30">
                      <td className="py-3 px-3 text-gray-300 text-sm">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-3 text-amber-400 font-bold text-sm">USD {Number(p.amount).toFixed(2)}</td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase border ${getStatusBadge(p.status)}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-gray-500 text-sm">{p.paypal_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmbassadorDashboard;
